import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Col, Container, Row, Spinner } from 'reactstrap';
import AdminTablePagination from '../../../Components/Common/AdminTablePagination';
import { CharacterItem, CharacterSortProperty, getCharacters } from '../../../helpers/api/admin/character';
import { useAuthStore } from '../../../store/useAuthStore';
import CreateCharacterModal from './CreateCharacterModal';

type SortKey = 'name' | 'owner' | 'createdAt';
type SortDirection = 'ASC' | 'DESC';

const columns: { key: Exclude<SortKey, 'createdAt'>; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'owner', label: 'Owner' },
];

const Character = () => {
  document.title = 'Admin Colosseum | Character management';

  const { isAdmin } = useAuthStore();
  const admin = isAdmin();
  const [characters, setCharacters] = useState<CharacterItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCharacters, setTotalCharacters] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('ASC');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const loadCharacters = useCallback(async (
    page = currentPage,
    limit = pageSize,
    sort: CharacterSortProperty = sortKey === 'createdAt' ? 'createdAt' : 'name',
    order = sortDirection,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const data = await getCharacters({ page, limit, sort, order });
      setCharacters(data.items);
      setTotalCharacters(data.meta.totalItems);
      setTotalPages(Math.max(1, data.meta.totalPages));
    } catch (e: any) {
      setCharacters([]);
      setTotalCharacters(0);
      setTotalPages(1);
      setError(typeof e === 'string' ? e : e?.message || 'Failed to load characters');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, sortDirection, sortKey]);

  useEffect(() => {
    if (!admin) return;
    loadCharacters();
  }, [admin, loadCharacters, refreshVersion]);

  const fromRow = totalCharacters > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const toRow = totalCharacters > 0 ? Math.min(fromRow + characters.length - 1, totalCharacters) : 0;

  const handleSort = (key: SortKey) => {
    if (key === 'owner') return;
    setCurrentPage(1);
    if (key === sortKey) {
      setSortDirection((direction) => (direction === 'ASC' ? 'DESC' : 'ASC'));
      return;
    }
    setSortKey(key);
    setSortDirection('ASC');
  };

  if (!admin) {
    return <Navigate to={{ pathname: '/dashboard' }} />;
  }

  return (
    <React.Fragment>
      <div className="page-content admin-users-page admin-characters-page" data-cy="admin-characters-page">
        <Container fluid>
          <Row className="mb-3">
            <Col xs={12} className="admin-characters-heading">
              <h4 className="mb-0"><span className="gradient-text-brand">Characters</span></h4>
              <Button type="button" onClick={() => setCreateModalOpen(true)} data-cy="character-create">
                <i className="ri-add-line me-1" aria-hidden="true" /> Create
              </Button>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="admin-users-panel">
                <div className="admin-users-table-wrap">
                  <table className="admin-users-table" data-cy="characters-table">
                    <thead>
                      <tr>
                        {columns.map((column) => {
                          const active = sortKey === column.key;
                          const sortable = column.key === 'name';
                          const sortLabel = sortable && active ? (sortDirection === 'ASC' ? 'ascending' : 'descending') : 'none';

                          return (
                            <th key={column.key} scope="col" aria-sort={sortLabel}>
                              <button
                                type="button"
                                onClick={() => handleSort(column.key)}
                                disabled={!sortable}
                                data-cy={`characters-sort-${column.key}`}
                              >
                                {column.label}
                                {sortable && active && (
                                  <span className="ms-1" aria-hidden="true">
                                    {sortDirection === 'ASC' ? '↑' : '↓'}
                                  </span>
                                )}
                              </button>
                            </th>
                          );
                        })}
                        <th scope="col">Avatar</th>
                        <th scope="col" className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={columns.length + 2} className="text-center">
                            <Spinner size="sm" className="me-2" /> Loading characters...
                          </td>
                        </tr>
                      ) : characters.length > 0 ? (
                        characters.map((character, index) => (
                          <tr key={character.id ?? index} data-cy="characters-row">
                            <td>{character.name}</td>
                            <td>{character.owner?.username || '-'}</td>
                            <td>
                              {character.avatar ? (
                                <img
                                  src={character.avatar}
                                  alt={`${character.name} avatar`}
                                  className="admin-character-avatar"
                                />
                              ) : (
                                <span className="admin-character-avatar admin-character-avatar--fallback" aria-label={`${character.name} has no avatar`}>
                                  {character.name.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="admin-character-edit"
                                aria-label={`Edit ${character.name}`}
                                data-cy="character-edit"
                              >
                                <i className="ri-pencil-line" aria-hidden="true" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columns.length + 2} className="text-center text-muted">No characters found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="admin-users-footer admin-characters-footer">
                  <div className="admin-users-page-size">
                    <label htmlFor="characterPageSize">Rows</label>
                    <select
                      id="characterPageSize"
                      className="form-select"
                      data-cy="characters-page-size"
                      value={pageSize}
                      onChange={(e) => {
                        setCurrentPage(1);
                        setPageSize(parseInt(e.target.value, 10));
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <span data-cy="characters-pagination-summary">
                      Showing {fromRow}-{toRow} of {totalCharacters} characters
                    </span>
                  </div>
                  <AdminTablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    disabled={loading}
                    onPageChange={setCurrentPage}
                    nextDataCy="characters-next-page"
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <CreateCharacterModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreated={() => {
            setCurrentPage(1);
            setSortKey('createdAt');
            setSortDirection('DESC');
            setRefreshVersion((version) => version + 1);
          }}
        />
      </div>
    </React.Fragment>
  );
};

export default Character;
